import { ReactNode, useEffect } from "react";
import { QueryClient, QueryClientProvider, MutationCache } from "@tanstack/react-query";
import { TooltipProvider } from "./Tooltip";
import { ThemeModeProvider } from "../helpers/themeMode";
import { ThemeColorProvider } from "../helpers/themeColor";
import { SonnerToaster } from "./SonnerToaster";
import { ScrollToHashElement } from "./ScrollToHashElement";
import { AuthProvider } from "../helpers/useAuth";
import { OrganizationProvider } from "../helpers/useOrganization";
import { DisplayCurrencyProvider } from "../helpers/useDisplayCurrency";
import { SplashScreen } from "./SplashScreen";
import { OAuthDeepLinkHandler } from "./OAuthDeepLinkHandler";
import { FlootRealtimeProvider } from "./FlootRealtimeProvider";
import { PushRegistrar } from "./PushRegistrar";
import { useAndroidBackButton } from "../helpers/useAndroidBackButton";
import { ErrorBoundary } from "./ErrorBoundary";
import styles from "./_globalContextProviders.module.css";

const mutationCache = new MutationCache({
  onError: (error) => {
    if (error instanceof Error && error.message === 'TRIAL_EXPIRED') {
      window.dispatchEvent(new CustomEvent('trial-expired'));
    }
  },
});

const queryClient = new QueryClient({
  mutationCache,
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      refetchOnMount: false,
      refetchOnWindowFocus: false,
    },
  },
});

export const GlobalContextProviders = ({
  children,
}: {
  children: ReactNode;
}) => {
  useAndroidBackButton();

  useEffect(() => {
    // Lock orientation to portrait for native mobile apps
    // This API works in Capacitor WebView but silently fails in regular browsers
    try {
      const orientation = screen.orientation as any;
if (orientation && typeof orientation.lock === 'function') {
orientation.lock('portrait').catch(() => {
          // Silently fail on browsers that don't support orientation lock
        });
      }
    } catch (e) {
      // Silently fail
    }
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <FlootRealtimeProvider>
        <ThemeModeProvider>
          <ThemeColorProvider>
          <ScrollToHashElement />
          <AuthProvider>
            <OrganizationProvider>
              <DisplayCurrencyProvider>
                <TooltipProvider>
                  <ErrorBoundary>
                    {children}
                  </ErrorBoundary>
                <OAuthDeepLinkHandler />
                <SplashScreen />
                <SonnerToaster />
                <PushRegistrar />
                <div className={styles.landscapeBlocker}>
                  <svg
                    className={styles.icon}
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <rect x="5" y="2" width="14" height="20" rx="2" ry="2" />
                    <line x1="12" y1="18" x2="12.01" y2="18" />
                  </svg>
                  <h3>Please rotate your device</h3>
                  <p>This app is best viewed in portrait mode.</p>
                </div>
                  </TooltipProvider>
                </DisplayCurrencyProvider>
              </OrganizationProvider>
            </AuthProvider>
          </ThemeColorProvider>
        </ThemeModeProvider>
      </FlootRealtimeProvider>
    </QueryClientProvider>
  );
};
